// src/components/sections/ContactSection.jsx
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import contactbg from "../../assets/background/contactbg.png";
import SparkleButton from "../common/SparkleButton";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { sendContactEmail } from "../../service/contactService";

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScP5Ud4HEMZW7aesP64MlZEiMBC_SwyEpmWsXLJSO1KSoxT0Q/formResponse";

const sendToGoogleForm = (data) => {
  const formData = new FormData();
  formData.append("entry.247055991", data.name);
  formData.append("entry.1621713458", data.phone);
  formData.append("entry.48322771", data.email);
  formData.append("entry.1003085532", data.company);
  formData.append("entry.1071834042", data.message);

  return fetch(GOOGLE_FORM_URL, {
    method: "POST",
    mode: "no-cors",
    body: formData,
  });
};

export default function ContactSection() {
  const { t } = useTranslation();

  // ====== Giới hạn ký tự theo yêu cầu ======
  const MAX_NAME_LEN = 128;
  const MAX_EMAIL_LEN = 128;
  const MAX_COMPANY_LEN = 255;
  const MAX_MESSAGE_LEN = 255;
  const MIN_MESSAGE_LEN = 0; // tuỳ chỉnh nếu muốn

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({
    dialCode: "+84",
    flag: "https://flagcdn.com/w40/vn.png",
    name: "Vietnam",
  });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    name: false,
    phone: false,
    email: false,
    company: false,
    message: false,
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // fetch countries list (countries.dev thay restcountries v3.1 đã deprecated)
  useEffect(() => {
    fetch(
      "https://countries.dev/countries?fields=name,alpha2Code,callingCodes,flags&sort=name"
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Countries API ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const parsed = (Array.isArray(data) ? data : [])
          .map((c) => {
            const code = c.alpha2Code?.toLowerCase();
            const callingCode = String(c.callingCodes?.[0] || "").replace(
              /\D/g,
              ""
            );
            if (!code || !callingCode) return null;

            return {
              name: c.name,
              code,
              flag: `https://flagcdn.com/w40/${code}.png`,
              dialCode: `+${callingCode}`,
            };
          })
          .filter(Boolean);
        setCountries(parsed);
      })
      .catch((err) => console.error("Error fetching countries:", err));
  }, []);

  useEffect(() => {
    if (status?.type === "success") {
      const timer = setTimeout(() => setStatus(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // click outside → close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ===== Validation helpers =====
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function tooLongMsg(max) {
    return `${t("contact.form.maxChars") ?? "Tối đa"} ${max} ${
      t("contact.form.chars") ?? "ký tự"
    }.`;
  }
  function tooShortMsg(min) {
    return `${t("contact.form.minChars") ?? "Tối thiểu"} ${min} ${
      t("contact.form.chars") ?? "ký tự"
    }.`;
  }

  function validateField(name, value) {
    const v = (value ?? "").trim();

    switch (name) {
      case "name":
        if (!v) return t("contact.form.required");
        if (v.length > MAX_NAME_LEN) return tooLongMsg(MAX_NAME_LEN);
        return "";

      case "email":
        if (!v) return t("contact.form.required");
        if (!emailRegex.test(v)) return t("contact.form.invalidEmail");
        if (v.length > MAX_EMAIL_LEN) return tooLongMsg(MAX_EMAIL_LEN);
        return "";

      case "message":
        if (!v) return t("contact.form.required");
        if (v.length < MIN_MESSAGE_LEN) return tooShortMsg(MIN_MESSAGE_LEN);
        if (v.length > MAX_MESSAGE_LEN) return tooLongMsg(MAX_MESSAGE_LEN);
        return "";

      case "phone": {
        // phone optional: chỉ validate khi có nhập
        if (!v) return "";
        const phoneDigits = v.replace(/\D/g, "");
        if (phoneDigits.length !== 10) return t("contact.form.invalidPhone");
        return "";
      }

      case "company":
        // optional: chỉ kiểm tra độ dài nếu có nhập
        if (v && v.length > MAX_COMPANY_LEN) return tooLongMsg(MAX_COMPANY_LEN);
        return "";

      default:
        return "";
    }
  }

  function validateAll(nextForm) {
    const nextErrors = {};
    ["name", "email", "message", "phone", "company"].forEach((field) => {
      const msg = validateField(field, nextForm[field]);
      if (msg) nextErrors[field] = msg;
    });
    return nextErrors;
  }

  // ===== Handlers =====
  const handleChange = (e) => {
    const { name, value } = e.target;

    // limit + numbers for phone
    if (name === "phone") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      const nextForm = { ...form, phone: digits };
      setForm(nextForm);
      if (touched.phone || errors.phone) {
        setErrors((prev) => ({
          ...prev,
          phone: validateField("phone", digits),
        }));
      }
      return;
    }

    if (name === "message") {
      const val = value.slice(0, MAX_MESSAGE_LEN);
      const nextForm = { ...form, message: val };
      setForm(nextForm);
      if (touched.message || errors.message) {
        setErrors((prev) => ({
          ...prev,
          message: validateField("message", val),
        }));
      }
      return;
    }

    if (name === "name") {
      const val = value.slice(0, MAX_NAME_LEN);
      const nextForm = { ...form, name: val };
      setForm(nextForm);
      if (touched.name || errors.name) {
        setErrors((prev) => ({ ...prev, name: validateField("name", val) }));
      }
      return;
    }

    if (name === "email") {
      const val = value.slice(0, MAX_EMAIL_LEN);
      const nextForm = { ...form, email: val };
      setForm(nextForm);
      if (touched.email || errors.email) {
        setErrors((prev) => ({ ...prev, email: validateField("email", val) }));
      }
      return;
    }

    if (name === "company") {
      const val = value.slice(0, MAX_COMPANY_LEN);
      const nextForm = { ...form, company: val };
      setForm(nextForm);
      if (touched.company || errors.company) {
        setErrors((prev) => ({
          ...prev,
          company: validateField("company", val),
        }));
      }
      return;
    }

    // fallback
    const nextForm = { ...form, [name]: value };
    setForm(nextForm);
    if (touched[name] || errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, form[name]) }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // mark all touched
    setTouched({
      name: true,
      phone: true,
      email: true,
      company: true,
      message: true,
    });

    const newErrors = validateAll(form);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // format phone (optional)
    let phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.startsWith("0")) phoneDigits = phoneDigits.substring(1);

    let formattedPhone = form.phone.trim();
    if (!formattedPhone.startsWith(selectedCountry.dialCode) && phoneDigits) {
      formattedPhone = `${selectedCountry.dialCode}${phoneDigits}`;
    }

    const finalData = { ...form, phone: formattedPhone };

    try {
      setLoading(true);
      setStatus(null);
      // Mail quyết định success/error; Google Form dùng no-cors nên không chặn UI
      await Promise.all([
        sendContactEmail(finalData),
        sendToGoogleForm(finalData).catch((err) => {
          console.error("Lỗi gửi Google Form:", err);
        }),
      ]);
      setStatus({ type: "success", msg: t("contact.form.success") });
      setForm({ name: "", phone: "", email: "", company: "", message: "" });
      setTouched({
        name: false,
        phone: false,
        email: false,
        company: false,
        message: false,
      });
      setErrors({});
    } catch (err) {
      console.error("Lỗi gửi contact:", err);
      setStatus({ type: "error", msg: t("contact.form.error") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative isolate text-white bg-cover bg-center bg-no-repeat px-4 py-36 mobile:py-10 tabletpro:py-16 tabletair:py-16 tabletmini:py-16"
      style={{ backgroundImage: `url(${contactbg})` }}
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 tabletair:gap-8 tabletmini:gap-6 mobile:gap-10 mobile:px-4 tabletpro:px-10 tabletair:px-10 tabletmini:px-10">
        {/* Left */}
        <div className="flex flex-col gap-[49px]">
          <p className="text-[20px] mobile:text-[14px] text-brandBlue-400 tracking-wide uppercase font-medium">
            {t("contact.caption")}
          </p>
          <h2 className="text-[52px] font-normal tabletpro:text-[40px] tabletair:text-[28px] tabletmini:text-[24px] mobile:text-[24px]">
            {t("contact.title.line1")}
            <br />
            {t("contact.title.line2")}
          </h2>
          <div className="flex gap-2 items-center">
            <p className="text-[24px] font-normal tabletpro:text-[14px] tabletair:text-[12px] mobile:text-[16px] text-white">
              {t("contact.subtitle.prefix")}
            </p>
            <span className="bg-gradient-to-b from-[#006EEF] via-[#87BDFE] to-[#FFFFFF] bg-clip-text text-transparent text-[56px] tabletpro:text-[40px] tabletair:text-[28px] mobile:text-[32px] tabletmini:text-[20px] font-manrope font-extrabold">
              {t("contact.subtitle.brand")}
            </span>
          </div>
        </div>

        {/* Right */}
        <div className="rounded-[22px] w-[508px] mobile:w-[320px] tabletmini:w-[320px] tabletair:w-[340px] tabletpro:w-[410px] border border-[#006EEF] bg-transparent px-6 py-6 ml-auto mobile:ml-0 mobile:justify-self-center justify-self-end">
          <h3 className="text-[#FFFFFF] text-[24px] mobile:text-[14px] font-light mb-6">
            {t("contact.form.title")}
          </h3>
          <form onSubmit={submit} className="flex flex-col gap-[24px]">
            {/* Name */}
            <div className="relative">
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder=" "
                aria-label={t("contact.form.name")}
                maxLength={MAX_NAME_LEN}
                className={`
      w-full font-light bg-transparent rounded-0 outline-none border-b py-3 mobile:text-[14px]
      ${
        touched.name && errors.name
          ? "border-b border-[#F42F4C] focus:border-[#F42F4C]"
          : "border-b border-white focus:border-[#006EEF]"
      }
      text-white
    `}
              />
              {form.name.length === 0 && (
                <span
                  className={`
        absolute left-0 top-3 pointer-events-none mobile:text-[14px] font-light
        ${touched.name && errors.name ? "text-[#F42F4C]" : "text-white"}
      `}
                >
                  {t("contact.form.name")}{" "}
                  <span className="text-[#F42F4C]">*</span>
                </span>
              )}

              {/* Error + counter */}
              <div className="mt-1 font-light text-xs flex items-center justify-between">
                {touched.name && errors.name ? (
                  <p className="text-[14px] text-[#F42F4C]">{errors.name}</p>
                ) : (
                  <span className="invisible">.</span>
                )}
                <span
                  className={`${
                    touched.name && errors.name
                      ? "text-[#F42F4C]"
                      : "text-white"
                  }`}
                >
                  {form.name.length}/{MAX_NAME_LEN}
                </span>
              </div>
            </div>

            {/* Phone + dropdown */}
            <div className="flex flex-col">
              <div
                className={`flex items-center gap-3 border-b relative ${
                  touched.phone && errors.phone
                    ? "border-[#F42F4C] focus-within:border-[#F42F4C]"
                    : "border-white focus-within:border-[#006EEF]"
                }`}
                ref={dropdownRef}
              >
                {/* Trigger */}
                <div
                  className="flex items-center gap-2 py-3 cursor-pointer"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <img
                    src={selectedCountry.flag}
                    alt={`${selectedCountry.name} flag`}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span
                    className={`text-[16px] ${
                      touched.phone && errors.phone ? "text-[#F42F4C]" : ""
                    }`}
                  >
                    {selectedCountry.dialCode}
                  </span>
                  {isOpen ? (
                    <FaCaretUp
                      className={`w-4 h-4 ${
                        touched.phone && errors.phone
                          ? "text-[#F42F4C]"
                          : "text-white"
                      }`}
                    />
                  ) : (
                    <FaCaretDown
                      className={`w-4 h-4 ${
                        touched.phone && errors.phone
                          ? "text-[#F42F4C]"
                          : "text-white"
                      }`}
                    />
                  )}
                </div>

                {/* Dropdown */}
                {isOpen && (
                  <ul className="absolute top-full left-0 mt-2 max-h-60 w-56 overflow-y-auto rounded-md bg-[#1a1a1a] shadow-lg z-50">
                    {countries.map((c) => (
                      <li
                        key={c.code}
                        className="flex items-center gap-2 px-3 py-2 text-white hover:bg-[#333] cursor-pointer"
                        onClick={() => {
                          setSelectedCountry(c);
                          setIsOpen(false);
                        }}
                      >
                        <img
                          src={c.flag}
                          alt={`${c.name} flag`}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                        <span className="shrink-0">{c.dialCode}</span>
                        <span className="truncate">{c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Phone input */}
                <div className="flex-1">
                  <input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={10}
                    placeholder={t("contact.form.phone")}
                    className={`w-full mobile:text-[14px] font-light bg-transparent rounded-0 outline-none py-3 caret-[#006EEF] selection:bg-[#006EEF]/30 ${
                      touched.phone && errors.phone
                        ? "placeholder-[#F42F4C]"
                        : "placeholder:text-white"
                    } text-white`}
                    onKeyDown={(e) => {
                      const allowed =
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                          "Home",
                          "End",
                        ].includes(e.key) || /^\d$/.test(e.key);
                      if (!allowed) e.preventDefault();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = (e.clipboardData.getData("text") || "")
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      if (pasted) {
                        setForm((p) => ({ ...p, phone: pasted }));
                        setTouched((p) => ({ ...p, phone: true }));
                        setErrors((prev) => ({
                          ...prev,
                          phone: validateField("phone", pasted),
                        }));
                      }
                    }}
                  />
                </div>
              </div>
              {touched.phone && errors.phone && (
                <p className="mt-1 text-sm font-light text-[#F42F4C]">
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="relative">
              <input
                id="email"
                name="email"
                type="text"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder=" "
                aria-label={t("contact.form.email")}
                maxLength={MAX_EMAIL_LEN}
                className={`
      w-full font-light bg-transparent outline-none rounded-0 border-b py-3 mobile:text-[14px]
      ${
        touched.email && errors.email
          ? "border-b border-[#F42F4C] focus:border-[#F42F4C]"
          : "border-b border-white focus:border-[#006EEF]"
      }
      text-white
    `}
              />
              {form.email.length === 0 && (
                <span
                  className={`
        pointer-events-none absolute left-0 top-3
        mobile:text-[14px] font-light
        ${touched.email && errors.email ? "text-[#F42F4C]" : "text-white"}
      `}
                >
                  {t("contact.form.email")}{" "}
                  <span className="text-[#F42F4C]">*</span>
                </span>
              )}

              {/* Error + counter */}
              <div className="mt-1 font-light text-xs flex items-center justify-between">
                {touched.email && errors.email ? (
                  <p className="text-[14px] text-[#F42F4C]">{errors.email}</p>
                ) : (
                  <span className="invisible">.</span>
                )}
                <span
                  className={`${
                    touched.email && errors.email
                      ? "text-[#F42F4C]"
                      : "text-white"
                  }`}
                >
                  {form.email.length}/{MAX_EMAIL_LEN}
                </span>
              </div>
            </div>

            {/* Company (optional) */}
            <div>
              <input
                id="company"
                name="company"
                value={form.company}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("contact.form.company")}
                maxLength={MAX_COMPANY_LEN}
                className={`
      w-full font-light bg-transparent outline-none rounded-0 border-b py-3 mobile:text-[14px]
      ${
        touched.company && errors.company
          ? "border-b border-[#F42F4C] focus:border-[#F42F4C] placeholder-[#F42F4C]"
          : "border-b border-white focus:border-[#006EEF] placeholder:text-white"
      }
    `}
              />

              {/* Error + counter */}
              <div className="mt-1 font-light text-xs flex items-center justify-between">
                {touched.company && errors.company ? (
                  <p className="text-[14px] text-[#F42F4C]">{errors.company}</p>
                ) : (
                  <span className="invisible">.</span>
                )}
                <span
                  className={`${
                    touched.company && errors.company
                      ? "text-[#F42F4C]"
                      : "text-white"
                  }`}
                >
                  {form.company.length}/{MAX_COMPANY_LEN}
                </span>
              </div>
            </div>

            {/* Message */}
            <div className="relative">
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder=" "
                aria-label={t("contact.form.message")}
                maxLength={MAX_MESSAGE_LEN}
                className={`
                  w-full font-light bg-transparent outline-none rounded-0 border-b py-3 resize-none mobile:text-[14px]
                  ${
                    touched.message && errors.message
                      ? "border-b border-[#F42F4C] focus:border-[#F42F4C]"
                      : "border-b border-white focus:border-[#006EEF]"
                  }
                  text-white
                `}
              />

              {/* Fake placeholder */}
              {form.message.length === 0 && (
                <span
                  className={`
                    pointer-events-none absolute left-0 top-3
                    mobile:text-[14px] font-light
                    ${
                      touched.message && errors.message
                        ? "text-[#F42F4C]"
                        : "text-white"
                    }
                  `}
                >
                  {t("contact.form.message")}{" "}
                  <span className="text-[#F42F4C]">*</span>
                </span>
              )}

              {/* Error + counter chung 1 hàng */}
              <div className="mt-1 font-light text-xs flex items-center justify-between">
                {touched.message && errors.message ? (
                  <p className="text-[14px] text-[#F42F4C]">{errors.message}</p>
                ) : (
                  <span className="invisible">.</span>
                )}
                <span
                  className={`${
                    form.message.length > MAX_MESSAGE_LEN ||
                    (touched.message && errors.message)
                      ? "text-[#F42F4C]"
                      : "text-white"
                  }`}
                >
                  {form.message.length}/{MAX_MESSAGE_LEN}
                </span>
              </div>
            </div>

            {/* Status */}
            {status && (
              <p
                className={`text-sm ${
                  status.type === "success"
                    ? "text-green-400"
                    : "text-[#F42F4C]"
                }`}
              >
                {status.msg}
              </p>
            )}

            {/* Submit */}
            <div className="pt-2">
              <SparkleButton
                type="submit"
                className={`font-light mobile:text-[14px] px-6 py-3 ${
                  loading
                    ? "!bg-white/20 !border-white font-light !cursor-not-allowed pointer-events-none"
                    : ""
                }`}
                onClick={(e) => {
                  if (loading) e.preventDefault();
                }}
              >
                {loading ? t("contact.form.sending") : t("contact.form.submit")}
              </SparkleButton>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
